import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { IoWarningOutline, IoMailOutline, IoLockClosedOutline, IoEyeOutline, IoEyeOffOutline, IoCheckmarkCircleOutline, IoGameControllerOutline } from 'react-icons/io5';
import './Login.css';
import { API_URL } from '../utils/networkConfig';

const Login = () => {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear errors when user starts typing
    if (error) setError('');
    if (verificationError) setVerificationError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setVerificationError(null);
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        // Store JWT token and user info in localStorage with correct key names
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('userRole', data.user.role_id);
        localStorage.setItem('userId', data.user.id);
        localStorage.setItem('userName', data.user.name);
        localStorage.setItem('userEmail', data.user.email);
        
        console.log('Login successful! Token stored:', data.token);
        console.log('User role:', data.user.role_id);
        
        // Dispatch custom event to notify other components of login
        window.dispatchEvent(new CustomEvent('authChange'));
        
        // Small delay to ensure localStorage is updated
        setTimeout(() => {
          navigate('/');
        }, 100);
      } else {
        // Check if this is a verification error
        if (data.requiresVerification) {
          setVerificationError({
            message: data.error,
            email: data.email,
            verificationEmailSent: data.verificationEmailSent
          });
        } else {
          setError(data.error || 'Login failed.');
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Network error. Please check if the server is running.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!verificationError?.email) {
      setError('Email address not available for resending verification.');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationError.email }),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setVerificationError({
          ...verificationError,
          message: 'Verification email sent! Please check your email.',
          verificationEmailSent: true
        });
      } else {
        setError(data.error || 'Failed to resend verification email.');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-background">
        <div className="stars"></div>
        <div className="stars2"></div>
        <div className="stars3"></div>
      </div>
      
      <div className="login-container">
        <div className="login-card">
          <div className="login-card-header">
            <h1>Sign in to Join the Game Show!</h1>
          </div>
          
          <form onSubmit={handleSubmit} className="login-form">
            <div className="input-group">
              <div className="input-wrapper">
                <IoMailOutline className="input-icon" />
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={form.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                {form.email && (
                  <IoCheckmarkCircleOutline className="check-icon" />
                )}
              </div>
            </div>
            
            <div className="input-group">
              <div className="input-wrapper">
                <IoLockClosedOutline className="input-icon" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="show-password-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            
            <button type="submit" disabled={loading} className="login-btn">
              {loading ? (
                <>
                  <div className="spinner"></div>
                  Signing In...
                </>
              ) : (
                <>
                  <IoGameControllerOutline className="btn-icon" />
                  Let's Play!
                </>
              )}
            </button>
          </form>
          
          {verificationError && (
            <div className="verification-error">
              <div className="verification-status info">
                <IoMailOutline className="status-icon info" />
                <div className="status-message">
                  <h3>Email Verification Required</h3>
                  <p>{verificationError.message}</p>
                </div>
              </div>
              
              <div className="verification-actions">
                <div className="email-check-message">
                  <p>
                    A verification email has been sent to <strong>{verificationError.email}</strong>
                  </p>
                  <p>
                    Please check your email and click the verification link to activate your account.
                  </p>
                </div>

                <div className="resend-section">
                  <p>Still didn't receive the email?</p>
                  <button 
                    onClick={handleResendVerification}
                    disabled={loading}
                    className="resend-btn"
                  >
                    {loading ? (
                      <>
                        <div className="spinner"></div>
                        Sending...
                      </>
                    ) : (
                      'Resend Verification Email'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {error && (
            <div className="error-message">
              <IoWarningOutline className="error-icon" />
              {error}
            </div>
          )}
          
          <div className="login-links">
            <p>Don't have an account? <Link to="/register" className="register-link">Register here</Link></p>
            <p>Forgot your password? <Link to="/forgot-password" className="forgot-link">Reset it</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 